import { Button } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { FacebookLogo, InstagramLogo, LinkedinLogo, TiktokLogo } from "@phosphor-icons/react";
import { ArrowRight, Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";

import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";

import { Decoration } from "../sections/hero/decoration";

export const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Scrolls to a home section, or navigates to the home page with the section
  // hash when we are on another page.
  const scrollToSection = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();

    if (pathname === "/") {
      document.querySelector(`#${sectionId}`)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${sectionId}`);
    }
  };
  return (
    <footer className="relative bg-background">
      <div className="bg-muted/20 border-t border-border/40">
        <div className="container mx-auto py-10">
          <div className="grid gap-8 md:grid-cols-2">
            <div className="flex flex-col justify-center space-y-4">
              <h2 className="text-2xl font-bold tracking-tight">{t`Ready to boost your career?`}</h2>
              <p className="text-muted-foreground">
                {t`Create a resume that makes a difference and maximize your interview chances.`}
              </p>
            </div>
            <div className="flex items-center justify-end space-x-4">
              <Link to="/auth/register">
                <Button size={"md"}>
                  {t`Create my resume for free`}
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-12">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="col-span-12 flex flex-col space-y-4 md:col-span-4">
            <div className="flex items-center space-x-2">
              <Logo size={120} />
            </div>
            <p className="text-muted-foreground max-w-sm text-sm">
              {t`Our resume creation platform uses AI and the latest technologies to help you overcome ATS barriers and secure more interviews.`}
            </p>
            <div className="flex space-x-4 pt-2">
              <a
                href="#"
                aria-label="Facebook"
                className="text-muted-foreground hover:bg-muted rounded-full p-2 transition-colors hover:text-foreground"
              >
                <FacebookLogo className="size-4" />
              </a>
              <a
                href="#"
                aria-label="Twitter"
                className="text-muted-foreground hover:bg-muted rounded-full p-2 transition-colors hover:text-foreground"
              >
                <TiktokLogo className="size-4" />
              </a>
              <a
                href="#"
                aria-label="LinkedIn"
                className="text-muted-foreground hover:bg-muted rounded-full p-2 transition-colors hover:text-foreground"
              >
                <LinkedinLogo className="size-4" />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="text-muted-foreground hover:bg-muted rounded-full p-2 transition-colors hover:text-foreground"
              >
                <InstagramLogo className="size-4" />
              </a>
            </div>
          </div>

          <div className="col-span-6 md:col-span-2">
            <h3 className="mb-4 text-sm font-medium uppercase tracking-wider">{t`Product`}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="#features"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  onClick={scrollToSection("features")}
                >
                  {t`Features`}
                </a>
              </li>
              <li>
                <a
                  href="#templates"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  onClick={scrollToSection("templates")}
                >
                  {t`Resume Templates`}
                </a>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Pricing`}
                </Link>
              </li>
              <li>
                <a
                  href="#testimonials"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                  onClick={scrollToSection("testimonials")}
                >
                  {t`Testimonials`}
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <h3 className="mb-4 text-sm font-medium uppercase tracking-wider">{t`Resources`}</h3>
            <ul className="space-y-3 text-sm">
              {/* <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Blog`}
                </Link>
              </li> */}
              <li>
                <Link
                  to="/guides"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Career Guides`}
                </Link>
              </li>
              <li>
                <Link
                  to="/ats-checker"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`ATS Checker`}
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`FAQ`}
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <h3 className="mb-4 text-sm font-medium uppercase tracking-wider">{t`Company`}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/about"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`About`}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Contact`}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Privacy Policy`}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Terms of Use`}
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <h3 className="mb-4 text-sm font-medium uppercase tracking-wider">{t`Support`}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/help"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Help Center`}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-muted-foreground flex items-center transition-colors hover:text-foreground"
                >
                  <Mail className="mr-2 size-3" /> {t`Contact Us`}
                </Link>
              </li>
              <li>
                <Link
                  to="/feedback"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t`Suggestions`}
                </Link>
              </li>
              <li className="flex items-center space-x-3 pt-3">
                <LocaleSwitch />
                <ThemeSwitch />
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-border/30">
        <div className="text-muted-foreground container mx-auto flex flex-col items-center justify-between py-6 text-sm md:flex-row">
          <div className="mb-4 flex items-center md:mb-0">
            <p>
              © {new Date().getFullYear()} Izicvs {t`by`}{" "}
              <a
                href="https://nextilabs.com"
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium transition-colors hover:text-foreground"
              >
                Nextilabs
              </a>
              . {t`All rights reserved.`}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <Link to="/cookies" className="transition-colors hover:text-foreground">
              {t`Cookie Policy`}
            </Link>
            <div className="h-4 w-px bg-border/60"></div>
            <Link to="/sitemap" className="transition-colors hover:text-foreground">
              {t`Sitemap`}
            </Link>
          </div>
        </div>
      </div>
      <Decoration.Grid />
      <Decoration.Gradient animated />
    </footer>
  );
};
